import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";

let ffmpeg = null;
let isInitializing = false;

export const initFFmpeg = async (onProgress) => {
  if (ffmpeg) return ffmpeg;

  if (isInitializing) {
    // Wait for existing initialization to complete with timeout
    const startTime = Date.now();
    while (isInitializing && !ffmpeg && Date.now() - startTime < 30000) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (ffmpeg) return ffmpeg;
    // If still initializing after 30 seconds, reset and try again
    isInitializing = false;
  }

  isInitializing = true;

  // Use more reliable CDN sources with shorter timeouts
  const cdnSources = [
    {
      name: "jsdelivr-esm",
      baseURL: "https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/esm",
    },
    {
      name: "unpkg-esm",
      baseURL: "https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm",
    },
    {
      name: "jsdelivr-umd-fallback",
      baseURL: "https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/umd",
    },
  ];

  for (const source of cdnSources) {
    try {
      console.log(`Trying FFmpeg initialization with ${source.name}...`);
      ffmpeg = new FFmpeg();

      // Add logging
      ffmpeg.on("log", ({ message }) => {
        console.log("FFmpeg log:", message);
      });

      ffmpeg.on("progress", ({ progress }) => {
        console.log("FFmpeg progress:", Math.round(progress * 100) + "%");
        onProgress(Math.round(progress * 100));
      });

      // Try to load core files with shorter timeout
      console.log(`Loading FFmpeg core from ${source.name}...`);

      const coreURL = await toBlobURL(
        `${source.baseURL}/ffmpeg-core.js`,
        "text/javascript"
      );
      const wasmURL = await toBlobURL(
        `${source.baseURL}/ffmpeg-core.wasm`,
        "application/wasm"
      );

      const loadConfig = {
        coreURL,
        wasmURL,
      };

      // Reduced timeout to 15 seconds
      await Promise.race([
        ffmpeg.load(loadConfig),
        new Promise((_, reject) =>
          setTimeout(
            () =>
              reject(new Error(`Initialization timeout with ${source.name}`)),
            15000
          )
        ),
      ]);

      console.log(`FFmpeg initialized successfully with ${source.name}`);
      isInitializing = false;
      return ffmpeg;
    } catch (error) {
      console.error(`Failed to initialize FFmpeg with ${source.name}:`, error);
      ffmpeg = null;
      // Continue to next CDN source
    }
  }

  // If all CDN sources failed, try one more time with a simpler approach
  try {
    console.log("Trying simplified FFmpeg initialization...");
    ffmpeg = new FFmpeg();

    // Very basic initialization without custom CDN
    await Promise.race([
      ffmpeg.load(),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("Final initialization timeout")),
          10000
        )
      ),
    ]);

    console.log("FFmpeg initialized with default configuration");
    isInitializing = false;
    return ffmpeg;
  } catch (error) {
    console.error("Final initialization attempt failed:", error);
  }

  // If everything failed
  isInitializing = false;
  throw new Error(
    "Failed to initialize video processor. Please refresh the page and try again."
  );
};

export const trimVideoWithAudio = async (
  videoFile,
  startTime,
  endTime,
  rotation = 0,
  crop = null,
  onProgress = (progress) => {}
) => {
  console.log("Starting video processing...");

  try {
    const ffmpeg = await initFFmpeg(onProgress);

    const inputFileName = "input.mp4";
    const outputFileName = "output.mp4";

    console.log("Writing input file...");
    // Write input file with timeout
    const fileData = await Promise.race([
      (async () => {
        if (typeof videoFile === "string") {
          console.log("Fetching video from URL:", videoFile);
          return await fetchFile(videoFile);
        } else {
          console.log("Processing uploaded file:", videoFile.name);
          return await fetchFile(videoFile);
        }
      })(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("File loading timeout")), 30000)
      ),
    ]);

    await ffmpeg.writeFile(inputFileName, fileData);
    console.log("Input file written successfully");

    // Build FFmpeg command
    const args = [
      "-i",
      inputFileName,
      "-ss",
      startTime.toString(),
      "-t",
      (endTime - startTime).toString(),
      "-c:v",
      "libx264",
      "-c:a",
      "aac",
      "-preset",
      "ultrafast",
      "-avoid_negative_ts",
      "make_zero",
    ];

    // Add rotation and crop filters if needed
    const filters = [];
    if (rotation > 0) {
      const rotationFilter =
        rotation === 90
          ? "transpose=1"
          : rotation === 180
          ? "transpose=1,transpose=1"
          : "transpose=2";
      filters.push(rotationFilter);
    }
    if (crop) {
      filters.push(`crop=${crop.width}:${crop.height}:${crop.x}:${crop.y}`);
    }

    if (filters.length > 0) {
      args.push("-vf", filters.join(","));
    }

    args.push("-y", outputFileName);

    console.log("FFmpeg command:", args.join(" "));

    // Execute FFmpeg command with timeout
    console.log("Starting FFmpeg processing...");
    await Promise.race([
      ffmpeg.exec(args),
      new Promise((_, reject) =>
        setTimeout(
          () =>
            reject(
              new Error(
                "Processing timeout - video might be too large or complex"
              )
            ),
          60000
        )
      ),
    ]);

    console.log("FFmpeg processing completed");

    // Read output file
    console.log("Reading output file...");
    const data = await ffmpeg.readFile(outputFileName);

    if (!data || data.length === 0) {
      throw new Error("Output file is empty - processing may have failed");
    }

    console.log("Output file size:", data.length, "bytes");

    // Clean up
    try {
      await ffmpeg.deleteFile(inputFileName);
      await ffmpeg.deleteFile(outputFileName);
      console.log("Cleanup completed");
    } catch (cleanupError) {
      console.warn("Cleanup warning:", cleanupError);
    }

    return new Blob([data], { type: "video/mp4" });
  } catch (error) {
    console.error("Video processing error:", error);

    // Reset initialization flag on error
    isInitializing = false;

    // Cleanup on error if ffmpeg exists
    if (ffmpeg) {
      try {
        await ffmpeg.deleteFile("input.mp4").catch(() => {});
        await ffmpeg.deleteFile("output.mp4").catch(() => {});
      } catch (cleanupError) {
        console.warn("Error cleanup failed:", cleanupError);
      }
    }

    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error("Unknown error during video processing");
    }
  }
};
